"use client";

import type { VariantProps } from "class-variance-authority";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

export type ChangingButtonAction = Omit<
  ComponentProps<"button">,
  "aria-label" | "children"
> & {
  ariaLabel: string;
  icon: ReactNode;
};

type ButtonAppearanceProps = Pick<
  VariantProps<typeof buttonVariants>,
  "mode" | "size" | "variant"
>;

export type ChangingButtonProps = ButtonAppearanceProps &
  Omit<ComponentProps<"div">, "children"> & {
    children: ReactNode;
    disabled?: boolean;
    leadingAction: ChangingButtonAction;
    trailingAction: ChangingButtonAction;
  };

function actionSizeClass(
  size: VariantProps<typeof buttonVariants>["size"]
): string {
  switch (size) {
    case "sm":
    case "icon-sm":
    case "rounded-sm":
      return "min-w-6 px-1.5";
    case "lg":
    case "icon-lg":
    case "rounded-lg":
      return "min-w-10 px-2.5";
    default:
      return "min-w-8 px-2";
  }
}

function disabledClass(
  mode: VariantProps<typeof buttonVariants>["mode"],
  variant: VariantProps<typeof buttonVariants>["variant"]
): string {
  if (mode !== "D2C") {
    return "opacity-50";
  }

  switch (variant) {
    case "outline":
    case "outline-no-hover":
      return "border-alabastergrey bg-background text-alabastergrey hover:bg-background active:border-alabastergrey active:text-alabastergrey [&>button:disabled]:opacity-100";
    case "ghost":
    case "link":
      return "bg-transparent text-alabastergrey hover:bg-transparent active:text-alabastergrey [&>button:disabled]:opacity-100";
    case "default":
    case "secondary":
    case "destructive":
    case null:
    case undefined:
      return "bg-alabastergrey text-white hover:bg-alabastergrey active:bg-alabastergrey [&>button:disabled]:opacity-100";
    default:
      return "opacity-50";
  }
}

interface ActionProps {
  action: ChangingButtonAction;
  disabled: boolean;
  size: VariantProps<typeof buttonVariants>["size"];
}

function Action({ action, disabled, size }: Readonly<ActionProps>) {
  const {
    ariaLabel,
    className,
    disabled: actionDisabled,
    icon,
    type = "button",
    ...buttonProps
  } = action;

  return (
    <button
      {...buttonProps}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex h-full shrink-0 cursor-pointer items-center justify-center text-current outline-none transition-colors focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-inset disabled:pointer-events-none disabled:cursor-default disabled:opacity-50",
        actionSizeClass(size),
        className
      )}
      disabled={disabled || actionDisabled}
      type={type}
    >
      <span aria-hidden="true" className="inline-flex shrink-0">
        {icon}
      </span>
    </button>
  );
}

export function ChangingButton(
  props: Readonly<ChangingButtonProps>
): ReactNode {
  const {
    children,
    className,
    disabled = false,
    leadingAction,
    mode,
    role = "group",
    size,
    trailingAction,
    variant,
    ...groupProps
  } = props;

  return (
    <div
      {...groupProps}
      aria-disabled={disabled || undefined}
      className={cn(
        buttonVariants({ mode, size, variant }),
        "group cursor-default gap-0 px-0 py-0 focus-within:ring-2 focus-within:ring-current/30 focus-within:ring-offset-2",
        disabled && "pointer-events-none cursor-default",
        disabled && disabledClass(mode, variant),
        className
      )}
      role={role}
    >
      <Action action={leadingAction} disabled={disabled} size={size} />
      <span className="pointer-events-none inline-flex min-w-6 flex-1 items-center justify-center px-1 text-center">
        {children}
      </span>
      <Action action={trailingAction} disabled={disabled} size={size} />
    </div>
  );
}
